
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Cpu,
  MessageSquare,
  TrendingUp,
  AlertCircle,
  Clock,
  ArrowLeft
} from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { apiUrl } from '../../utils/api';

const data = [
  { name: '08:00', requests: 400 },
  { name: '10:00', requests: 1200 },
  { name: '12:00', requests: 900 },
  { name: '14:00', requests: 2400 },
  { name: '16:00', requests: 1800 },
  { name: '18:00', requests: 2800 },
  { name: '20:00', requests: 2100 },
];

const StatCard: React.FC<{ title: string, value: string, sub: string, icon: React.ReactNode, color: string }> = ({ title, value, sub, icon, color }) => (
  <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm transition-colors duration-300">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
        <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{value}</h3>
        <p className={`text-xs mt-2 font-medium ${sub.startsWith('+') ? 'text-emerald-600' : 'text-slate-400'}`}>
          {sub} <span className="text-slate-400 font-normal">vs last month</span>
        </p>
      </div>
      <div className={`p-3 rounded-xl ${color} bg-opacity-10`}>
        {React.cloneElement(icon as React.ReactElement<any>, { className: color.replace('bg-', 'text-') })}
      </div>
    </div>
  </div>
);

interface OverviewProps {
  selectedAgent?: { id: string; name: string };
}

const Overview: React.FC<OverviewProps> = ({ selectedAgent }) => {
  const navigate = useNavigate();
  const [totalUsers, setTotalUsers] = useState<string>('0');
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(true);
  const [activeAgentsCount, setActiveAgentsCount] = useState<string>('0');
  const [isLoadingAgents, setIsLoadingAgents] = useState<boolean>(true);
  const [recentActivities, setRecentActivities] = useState<any[]>([]);
  const [isLoadingActivity, setIsLoadingActivity] = useState<boolean>(true);
  const [conversationsCount, setConversationsCount] = useState<number>(0);
  const [isLoadingConversations, setIsLoadingConversations] = useState<boolean>(true);
  const [chartData, setChartData] = useState<any[]>([]);
  const [topAgents, setTopAgents] = useState<any[]>([]);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [timescale, setTimescale] = useState<string>('24h');
  const baseRequestCountRef = useRef<number | null>(null);
  const [displayNewActivity, setDisplayNewActivity] = useState<number>(0);

  const reportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchTotalUsers = async () => {
      try {
        setIsLoadingUsers(true);
        const endpoint = apiUrl('/api/auth/users');
        console.log('Fetching from endpoint:', endpoint);

        const response = await fetch(endpoint);
        console.log('API Response Status:', response.status);
        console.log('API Response OK:', response.ok);

        if (response.ok) {
          const users = await response.json();
          console.log('Users data received:', users);
          const userCount = Array.isArray(users) ? users.length : 0;
          console.log('Total user count:', userCount);
          // Format the number with commas for display
          setTotalUsers(userCount.toLocaleString());
        } else {
          const errorText = await response.text();
          console.error('Failed to fetch users. Status:', response.status, 'Error:', errorText);
          setTotalUsers('N/A');
        }
      } catch (error) {
        console.error('Error fetching users:', error);
        setTotalUsers('N/A');
      } finally {
        setIsLoadingUsers(false);
      }
    };

    const fetchActiveAgents = async () => {
      try {
        setIsLoadingAgents(true);
        const endpoint = apiUrl('/api/dashboard/agents/count');
        const response = await fetch(endpoint);
        if (response.ok) {
          const data = await response.json();
          setActiveAgentsCount(data.count.toString());
        } else {
          setActiveAgentsCount('N/A');
        }
      } catch (error) {
        console.error('Error fetching active agents:', error);
        setActiveAgentsCount('N/A');
      } finally {
        setIsLoadingAgents(false);
      }
    };

    const fetchRecentActivity = async () => {
      try {
        setIsLoadingActivity(true);
        const endpoint = apiUrl('/api/dashboard/activity');
        const response = await fetch(endpoint);
        if (response.ok) {
          const data = await response.json();
          setRecentActivities(data);
        }
      } catch (error) {
        console.error('Error fetching activity:', error);
      } finally {
        setIsLoadingActivity(false);
      }
    };

    const fetchConversations = async () => {
      try {
        setIsLoadingConversations(true);
        const endpoint = apiUrl('/api/dashboard/conversations/count');
        const response = await fetch(endpoint);
        if (response.ok) {
          const data = await response.json();
          setConversationsCount(parseInt(data.count) || 0);
        } else {
          setConversationsCount(0);
        }
      } catch (error) {
        console.error('Error fetching conversations:', error);
        setConversationsCount(0);
      } finally {
        setIsLoadingConversations(false);
      }
    };

    const fetchTopAgents = async () => {
      try {
        const endpoint = apiUrl('/api/dashboard/agents/top');
        const response = await fetch(endpoint);
        if (response.ok) {
          const data = await response.json();
          setTopAgents(data);
        }
      } catch (error) {
        console.error('Error fetching top agents:', error);
      }
    };

    const fetchChartData = async (currentTimescale: string) => {
      try {
        let url = `/api/dashboard/requests/over-time?timescale=${currentTimescale}`;
        if (selectedAgent && selectedAgent.id !== 'all') {
          url += `&agent_id=${selectedAgent.id}`;
        }
        const endpoint = apiUrl(url);
        const response = await fetch(endpoint);
        if (response.ok) {
          const fetchedData = await response.json();
          setChartData(fetchedData);

          // Use total requests sum for live counter
          const currentTotal = fetchedData.reduce((acc: number, item: any) => acc + item.requests, 0);
          if (baseRequestCountRef.current === null) {
            baseRequestCountRef.current = currentTotal;
          } else {
            setDisplayNewActivity(currentTotal - baseRequestCountRef.current);
          }
        }
      } catch (error) {
        console.error('Error fetching chart data:', error);
      }
    };

    const fetchAlerts = async () => {
      try {
        const endpoint = apiUrl('/api/dashboard/alerts');
        const response = await fetch(endpoint);
        if (response.ok) {
          const fetchedAlerts = await response.json();
          setAlerts(fetchedAlerts);
        }
      } catch (error) {
        console.error('Error fetching alerts:', error);
      }
    };

    const fetchAllData = () => {
      fetchTotalUsers();
      fetchActiveAgents();
      fetchRecentActivity();
      fetchConversations();
      fetchChartData(timescale);
      fetchAlerts();
      fetchTopAgents();
    };

    fetchAllData();
    const interval = setInterval(fetchAllData, 60000);
    return () => clearInterval(interval);
  }, [timescale, selectedAgent]);

  const handleDownloadReport = async () => {
    if (reportRef.current) {
      try {
        const canvas = await html2canvas(reportRef.current, {
          scale: 2,
          backgroundColor: '#f8fafc'
        });
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const fitWidth = pdfWidth - 20;
        const fitHeight = (canvas.height * fitWidth) / canvas.width;
        pdf.addImage(imgData, 'PNG', 10, 10, fitWidth, fitHeight);
        pdf.save('engazeiq-report.pdf');
      } catch (err) {
        console.error("Report generation failed:", err);
      }
    }
  };

  return (
    <div className="space-y-6" ref={reportRef}>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">System Overview</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Platform-wide health and usage performance metrics.</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleDownloadReport}
            className="px-4 py-2 bg-blue-600 rounded-lg text-sm font-medium text-white hover:bg-blue-700 shadow-lg shadow-blue-500/20 transition-all"
            data-html2canvas-ignore="true"
          >
            Download Report
          </button>

        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard title="Active Agents" value={activeAgentsCount} sub="+3 new" icon={<Cpu size={20} />} color="bg-blue-600" />
        <StatCard title="Total Users" value={totalUsers} sub="+12%" icon={<Users size={20} />} color="bg-indigo-600" />
        <StatCard title="Conversations" value={conversationsCount.toLocaleString()} sub="+8.4%" icon={<MessageSquare size={20} />} color="bg-emerald-600" />
        <StatCard title="Avg Latency" value="450ms" sub="-50ms" icon={<Clock size={20} />} color="bg-amber-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col transition-colors duration-300">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">Requests Over Time</h3>
              <div className="flex items-center gap-1.5 px-2 py-0.5 bg-blue-50 rounded-full">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></div>
                <span className="text-[10px] font-bold text-blue-600 uppercase tracking-tight">Live</span>
              </div>
            </div>
            <select
              value={timescale}
              onChange={(e) => setTimescale(e.target.value)}
              className="text-xs font-semibold border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-blue-500/20 transition-all cursor-pointer">
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
            </select>
          </div>

          <div className="flex-1 min-h-[300px] w-full bg-gradient-to-br from-slate-50 to-blue-50/30 dark:from-slate-900 dark:to-blue-900/10 rounded-2xl border border-slate-100 dark:border-slate-800 p-6 flex flex-col items-center justify-center relative overflow-hidden transition-colors duration-300">
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-400/5 rounded-full -mr-16 -mt-16 blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-indigo-400/5 rounded-full -ml-16 -mb-16 blur-3xl"></div>

            <div className="text-center z-10 mb-8">
              <div className="flex items-center justify-center gap-3 mb-1">
                <span className="text-5xl font-black text-slate-900 dark:text-slate-100 tracking-tighter transition-colors duration-300">
                  {displayNewActivity}
                </span>
                <div className="flex flex-col items-start">
                  <span className="text-blue-600 font-bold text-xs uppercase tracking-widest">New</span>
                  <span className="text-slate-400 font-medium text-[10px] uppercase tracking-widest leading-none">Activity</span>
                </div>
              </div>
              <div className="text-xs text-slate-500 font-medium italic">Events & requests recorded since you joined</div>
            </div>

            <div className="w-full mt-auto">
              <div className="overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-blue-200 scrollbar-track-transparent">
                <div className="flex items-end justify-center gap-4 min-w-max px-4">
                  {(chartData.length > 0 ? chartData : Array.from({ length: 12 }).map((_, i) => ({ name: `${i}:00`, requests: 0 }))).map((item, i) => {
                    const maxReq = Math.max(...chartData.map((d: any) => d.requests), 10);
                    const height = Math.max(8, (item.requests / (maxReq * 1.2)) * 140);
                    return (
                      <div key={i} className="flex flex-col items-center group">
                        <div className="relative w-3 flex flex-col items-center">
                          {item.requests > 0 && (
                            <div className="absolute -top-6 bg-slate-800 text-white text-[10px] py-0.5 px-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-20">
                              {item.requests} reqs
                            </div>
                          )}
                          <div
                            className={`w-full rounded-t-full transition-all duration-700 ease-out ${item.requests > 0
                              ? 'bg-gradient-to-t from-blue-600 to-blue-400 shadow-[0_0_10px_rgba(59,130,246,0.3)]'
                              : 'bg-slate-200 dark:bg-slate-800'
                              }`}
                            style={{ height: `${height}px` }}
                          ></div>
                        </div>
                        <span className="text-[10px] font-bold text-slate-400 mt-3 group-hover:text-slate-600 transition-colors">{item.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col transition-colors duration-300">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">Top Agents</h3>
            <button className="text-blue-600 dark:text-blue-400 text-xs font-bold hover:underline">View All</button>
          </div>
          <div className="space-y-4 flex-1">
            {topAgents.length === 0 ? (
              <div className="text-center py-4 text-slate-400 text-xs">No activity data yet</div>
            ) : (
              topAgents.map((agent, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer group">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-100 dark:bg-slate-800 rounded-lg flex items-center justify-center text-slate-600 dark:text-slate-400">
                      <Cpu size={18} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{agent.name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <div className={`w-1.5 h-1.5 rounded-full ${agent.latency < 5000 ? 'bg-emerald-500' : 'bg-amber-500'}`}></div>
                        <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">
                          {agent.latency > 0 ? `${agent.latency}ms` : 'Active'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-slate-700 dark:text-slate-300">{agent.count.toLocaleString()}</p>
                    <p className="text-[10px] text-slate-400">Total Tasks</p>
                  </div>
                </div>
              ))
            )}
          </div>
          <button className="mt-4 w-full py-2 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors flex items-center justify-center gap-2">
            View Analytics <TrendingUp size={16} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm transition-colors duration-300">
          <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-6">Recent Activity</h3>
          <div className="space-y-6">
            {isLoadingActivity ? (
              <div className="text-center py-4 text-slate-500 text-sm">Loading activity...</div>
            ) : recentActivities.length === 0 ? (
              <div className="text-center py-4 text-slate-500 text-sm">No recent activity</div>
            ) : (
              recentActivities.map((act, i) => (
                <div key={i} className="flex gap-4">
                  <div className="relative">
                    <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-600 dark:text-slate-400">
                      <Cpu size={20} />
                    </div>
                    {i !== recentActivities.length - 1 && <div className="absolute top-10 left-5 w-px h-8 bg-slate-100 dark:bg-slate-800"></div>}
                  </div>
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      <span className="font-bold text-slate-900 dark:text-slate-100">{act.user || 'System'}</span> {act.action} <span className="font-medium text-blue-600 dark:text-blue-400">{act.target}</span>
                    </p>
                    <p className="text-xs text-slate-400 mt-1">{new Date(act.time).toLocaleString()}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm transition-colors duration-300">
          <div className="flex items-center gap-2 mb-6">
            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">Alerts & Maintenance</h3>
            {alerts.length > 0 && (
              <span className="px-2 py-0.5 bg-red-100 text-red-600 text-[10px] font-bold rounded-full">
                {alerts.length} Active
              </span>
            )}
          </div>
          <div className="space-y-3">
            {alerts.length === 0 ? (
              <div className="p-8 text-center bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700">
                <p className="text-sm text-slate-500 font-medium text-emerald-600 dark:text-emerald-400">All systems operational</p>
              </div>
            ) : (
              alerts.map((alert, i) => (
                <div key={i} className={`p-4 rounded-2xl border flex items-start gap-3 ${alert.severity === 'critical' ? 'bg-red-50 dark:bg-red-900/10 border-red-100 dark:border-red-900/20' : 'bg-amber-50 dark:bg-amber-900/10 border-amber-100 dark:border-amber-900/20'
                  }`}>
                  <AlertCircle className={alert.severity === 'critical' ? 'text-red-500 mt-0.5' : 'text-amber-500 mt-0.5'} size={20} />
                  <div>
                    <p className={`text-sm font-bold ${alert.severity === 'critical' ? 'text-red-900 dark:text-red-100' : 'text-amber-900 dark:text-amber-100'}`}>{alert.title}</p>
                    <p className={`text-xs mt-0.5 ${alert.severity === 'critical' ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}`}>{alert.body}</p>
                    <span className="text-[10px] text-slate-400 mt-1 block uppercase font-medium">{alert.source}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview;
